import { useState } from 'react';
import { ClientSearchModal, type ClientSearchResult } from '@/components/ClientSearchModal';

/**
 * ContactPicker — a read-only field plus a button that opens the shared
 * ClientSearchModal (the same standard contact-search popup used by billing,
 * messages, and document sharing). Emits the selected contact's id.
 *
 * `contactNature` is left empty by default so any contact (client or prospect)
 * can be chosen — relationships and group membership are not client-only.
 */
export function ContactPicker({
  label,
  value,
  onChange,
  error,
  contactNature = '',
}: {
  label?: string;
  value: string;
  onChange: (contactId: string) => void;
  error?: string;
  contactNature?: string;
}) {
  const [open, setOpen] = useState(false);
  const [selectedName, setSelectedName] = useState('');

  const handleSelect = (client: ClientSearchResult) => {
    onChange(client.id);
    setSelectedName(client.displayName || client.fullName || '');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setSelectedName('');
  };

  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`flex-1 rounded-lg border bg-white px-3 py-2 text-left text-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-surface-dark-secondary ${
            error ? 'border-danger-500' : 'border-border dark:border-border-dark'
          } ${selectedName ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}`}
        >
          {selectedName || 'Search & select a contact...'}
        </button>
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="text-sm text-gray-500 hover:text-danger-500 dark:text-gray-400"
            aria-label="Clear selected contact"
          >
            Clear
          </button>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-danger-600">{error}</p>}

      {open && (
        <ClientSearchModal
          title="Search & Select Contact"
          contactNature={contactNature}
          onSelect={handleSelect}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
